import React, { FunctionComponent, InputHTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { InputSpinner } from '..';
import HelperTime from '../../helpers/HelperTime';

import './styles.scss';

interface IITem {
  item: any;
}

interface IProps {
  keyValue?: string;
  keyLabel?: string;
  list?: any[];
  Item?: FunctionComponent<IITem>;
  onSearch?: (filter: string) => Promise<any[]>;
  onSelect: (value?: string) => void;
}

type AutocompleteProps = IProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'onSelect' | 'onSearch'>;

const Autocomplete = ({ keyValue = '', keyLabel = 'name', list, Item, onSearch, onSelect, placeholder, "aria-label": ariaLabel, onChange, ...rest }: AutocompleteProps) => {
  const { t: localizer } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [isShow, setIsShow] = useState(false);
  const [input, setInput] = useState("");
  const [value, setValue] = useState<any>();

  useEffect(() => {
    if (list)
      handleGetList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  useEffect(() => {
    if (!onSearch) return;
    if (value && value[keyValue] === input) return;
    const timeOutId = setTimeout(() => {
      if (input === '') {
        if (keyValue && value && (value[keyValue] !== null && value[keyValue] !== undefined))
          onSelect();
        return null;
      } else {
        handleGetSearch()
      }
    }, HelperTime.secondsToMilliseconds(1));
    return () => clearTimeout(timeOutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  useEffect(() => {
    if(value){
        if(value[keyValue]){
            onSelect(value[keyValue]);
        } else {
            onSelect(JSON.stringify(value));
        }
    } else {
        onSelect('');
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, keyValue]);

  useEffect(() => {
    setInput("");
    setValue(undefined);
    setActive(0);
    setFiltered([]);
    setIsShow(false);
  }, [location.pathname]);

  const handleGetList = () => {
    const _filtered = list?.filter(
      x => x[keyValue].toLowerCase().indexOf(input.toLowerCase()) > -1
    );
    setActive(0);
    setFiltered(!_filtered ? [] : _filtered);
    setIsShow(true);
  }

  const handleGetSearch = () => {
    if (!onSearch || !input || input.length < 3) return;
    setLoading(true);
    onSearch(input).then(
      _list => {
        setActive(0);
        setFiltered(_list.sort((a, b) => a[keyLabel].localeCompare(b[keyLabel])));
        setIsShow(true);
      }).finally(() => setLoading(false));
  }

  const onClick = (register: any) => {
    setActive(0);
    setFiltered([]);
    setIsShow(false);
    setValue(register);
    setInput(register[keyLabel]);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setActive(0);
      setIsShow(false);
      setInput(filtered[active])
    }
    else if (e.key === 'ArrowUp') {
      return (active === 0) ? null : setActive(active - 1);
    }
    else if (e.key === 'ArrowDown') {
      return (active - 1 === filtered.length) ? null : setActive(active + 1);
    }
  };

  const renderAutocomplete = () => {
    if (isShow && input && filtered.length > 0) {
      return (
        <ul className="autocomplete">
          {filtered.sort((a, b) => a - b).map((register, index) => {
            let className;
            if (index === active) {
              className = "active";
            }
            return (
              <li key={`autocomplete-${register[keyValue] ?? index}`} className={className} onClick={() => onClick(register)}>
                {
                  Item
                    ? <Item item={register} />
                    : register[keyLabel]?.trim() ?? ''
                }
              </li>
            );
          })}
        </ul>
      );
    }
    return <></>;
  }

  return (
    <>
      <InputSpinner loading={loading}>
        <input
          id={rest.id}
          name={rest.name ?? rest.id}
          type="text"
          className="form-control input-autocomplete"
          onChange={e => {
            setInput(e.currentTarget.value)
            onChange && onChange(e)
          }}
          onKeyDown={onKeyDown}
          value={input}
          placeholder={placeholder ?? localizer("SearchDots")}
          aria-label={ariaLabel ?? placeholder ?? localizer("SearchDots")}
          {...rest}
        />
      </InputSpinner>
      {renderAutocomplete()}
    </>
  );
}

export default Autocomplete;
